import { DataGrid } from "@mui/x-data-grid/DataGrid";
import React from "react";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { convertPenniesToCurrency } from "@tlc/ls2pac/components/AccountMenu";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { useLocalizedShortDate } from "../../../../hooks/useLocalizedShortDate";
import { FinesGridRowModel } from "./FinesSharedView";
export interface FinesDesktopGridProps {
  rows: GridRowsProp<FinesGridRowModel>;
  pageSize: number;
  isStudent: boolean;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
}

export const FinesDesktopGrid: React.FC<FinesDesktopGridProps> = ({
  rows,
  pageSize,
  selectedIds,
  isStudent,
  setSelectedIds,
}) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { translate } = useLanguage();
  const formatDate = useLocalizedShortDate();

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "title",
      headerName: translate("reasonForFine"),
      renderHeader: () => (
        <Typography variant="body1" fontWeight={500}>
          {translate("reasonForFine")}
        </Typography>
      ),
      flex: 1,
      minWidth: 200,
      cellClassName: "wrapped-cell",
      sortable: false, // using LS sort
      filterable: false,

      renderCell: (params) => {
        const titleText = params.row.resource?.shortTitle ?? params.row.title;
        const resourceId = params.row.resource?.id;
        const hasShortAuthor =
          params.row.resource && params.row.resource.shortAuthor;
        const hasAuthorText = Boolean(params.row.author);
        const showAuthor = hasShortAuthor || hasAuthorText;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 1.5,
            }}
          >
            {/* --- Title by Author Line --- */}
            {(titleText || showAuthor) && (
              <Typography
                component="div"
                variant="body1"
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {/* Title */}
                {titleText &&
                  (resourceId ? (
                    <Link
                      tabIndex={params.tabIndex}
                      to={`/resource/${resourceId}`}
                    >
                      {`${titleText} `}
                    </Link>
                  ) : (
                    <span>{`${titleText} `}</span>
                  ))}

                {/* Author */}
                {(hasShortAuthor || hasAuthorText) && (
                  <Typography component="h4">
                    {translate("by")}{" "}
                    {params.row.resource?.shortAuthor || params.row.author}
                  </Typography>
                )}
              </Typography>
            )}

            {/* --- Fee Comment --- */}
            {params.row.feeComment && (
              <Typography
                component="div"
                variant="body1"
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {params.row.feeComment}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "date",
      headerName: translate("date"),
      renderHeader: () => (
        <Typography variant="body1" fontWeight={500}>
          {translate("date")}
        </Typography>
      ),
      minWidth: 100,
      maxWidth: 120,
      align: "right",
      headerAlign: "center",
      flex: 1,
      sortable: false,
      renderCell: (x) => <Typography>{formatDate(x.row.date)}</Typography>,
    },
    {
      field: "fine",
      headerName: translate("fine"),
      renderHeader: () => (
        <Typography variant="body1" fontWeight={500}>
          {translate("fine")}
        </Typography>
      ),
      minWidth: 90,
      maxWidth: 120,
      flex: 1,
      align: "right",
      headerAlign: "center",
      sortable: false, // using LS sort
      renderCell: (x) => (
        <Typography>{convertPenniesToCurrency(x.value)}</Typography>
      ),
    },
  ];

  const checkboxSelection =
    !isStudent && locationConfiguration?.pacSettings.ecommerceEnabled;
  return (
    <DataGrid
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setSelectedIds(newRowSelectionModel as number[]);
      }}
      rowSelectionModel={selectedIds}
      checkboxSelection={checkboxSelection}
      autoHeight
      disableVirtualization
      slotProps={{
        loadingOverlay: {
          variant: "circular-progress",
          noRowsVariant: "circular-progress",
        },
      }}
      getRowHeight={() => "auto"}
      getEstimatedRowHeight={() => 100}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: pageSize,
          },
        },
      }}
      pageSizeOptions={[5, 10, 25]}
      rows={rows}
      columns={columns}
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        selectedIds.includes(params.id as number) ? "custom-selected-row" : ""
      }
      keepNonExistentRowsSelected
      isRowSelectable={() => true}
      disableColumnSorting
      disableColumnFilter
      disableColumnMenu
      disableColumnResize
      hideFooter
      hideFooterPagination
      hideFooterSelectedRowCount
    />
  );
};
